/**
 * useTranslatedCaptionStaleness — is the baked caption track behind the current
 * translation, and the action to bring it up to date.
 *
 * Captions bake `TranslationPhrase.translatedValue` at produce time, so they
 * drift whenever a translation is edited without the track being re-synced. The
 * only thing closing that gap today is an automatic reaction that silently
 * rewrites the whole track — which also discards caption STRUCTURE edits
 * (synthetic line breaks, merged rows). This hook makes the drift visible so the
 * user can decide, instead of it being applied behind their back.
 *
 * SURFACE SCOPING: reads `useStore()`, so it resolves to the editor and karaoke
 * store of whichever surface it renders inside — each one overrides
 * `StoreContext` with a RootStore proxy (`clipTranscriptProxies.ts`). It must
 * therefore be called from a component rendered INSIDE that provider (i.e. from
 * `CaptionStalenessNotice`), never from the component that renders the provider —
 * that resolves the OUTER context and reads the wrong editor. `translationStore`
 * is deliberately not proxied; translations are global.
 *
 * No effect, no dep array, no once-per-video ref: everything is derived from
 * observables on each render, so an `observer` caller re-evaluates exactly when
 * the inputs change — including going quiet by itself once a refresh lands.
 */

import {
  makeTranslatedValueResolver,
  resolvePhraseCaptionTexts,
} from '@src/editor/services/karaokeSegmentation';
import { detectStaleTranslatedCaptions } from '@src/editor/services/syncTranslatedCaptionText';
import { useStore } from '@src/stores-v2/StoreContext';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback } from 'react';

import type { TranscriptToken } from '@src/editor/types';

const logger = LogManager.getLogger('feature:caption-staleness');

export interface TranslatedCaptionStalenessState {
  /** True when a full sync would change at least one caption row. */
  isStale: boolean;
  /**
   * Caption ROWS a sync would rewrite — never an edit count (one edit can span
   * several rows). For logging only; not for user-facing copy.
   */
  staleRowCount: number;
  /**
   * Re-text the whole track from the current translation. Undoable (unlike the
   * automatic path) because it is user-initiated and structurally lossy.
   */
  refresh: () => void;
}

const NOT_STALE = { isStale: false, staleRowCount: 0 } as const;

export function useTranslatedCaptionStaleness(): TranslatedCaptionStalenessState {
  const rootStore = useStore();
  const karaokeStore = rootStore.karaokeStore;
  const translationStore = rootStore.translationStore;

  const refresh = useCallback(() => {
    const editor = rootStore.editorStore?.editor;
    if (!editor) return;
    try {
      // Whole-track mode. `createUndoRedoEntry` defaults to true so the user can
      // undo the line breaks this discards.
      editor.commandSyncTranslatedCaptionText();
      // The command patches the caption clips in place and only marks the
      // editor dirty — no observer is told, so the karaoke store kept the
      // rows it had: the notice went on reporting stale, and the autosave
      // the command scheduled persisted those OLD rows, which is why a
      // refresh never stuck across a reopen. Re-read the store from the
      // editor first (the clip surface's proxy forwards this to its own
      // `refreshKaraokeState`), then repaint — CLAUDE.md: a commandX from a
      // UI surface pairs with an explicit sync, the command's own is debounced.
      rootStore.karaokeStore.syncFromEditor(editor);
      rootStore.videoSourceStore?.refreshSource();
    } catch (error) {
      logger.error('failed to refresh translated captions', {
        ...extractErrorInfo(error),
      });
    }
  }, [rootStore]);

  // The language the LOADED rows were produced for — not the transcript view's
  // language. `syncFromEditor` writes `karaokeLines` and `defaultKaraokeConfig`
  // from the same editor snapshot, so the rows and this language can never
  // describe different produces (no transient mismatch mid language-switch).
  const captionLanguage = karaokeStore.defaultKaraokeConfig?.language ?? null;
  // PLAT-11905 — the source-timed rows, shown or parked: while a dubbed
  // language shows its dub-timed rows (the voice's words, which never follow a
  // translation edit), the notice still describes the rows that do.
  const lines = karaokeStore.sourceCaptionLines;

  // Original-language captions don't read translations at all.
  if (!captionLanguage || isOriginalLang(captionLanguage)) {
    return { ...NOT_STALE, refresh };
  }
  // No track produced yet ⇒ nothing to be behind.
  if (lines.length === 0) return { ...NOT_STALE, refresh };

  // Absent while the translation is still loading (or was never generated), in
  // which case there is nothing authoritative to compare against yet. Reading
  // the observable here is what makes an `observer` re-evaluate once it lands.
  const phrases = translationStore.getTranslation(captionLanguage)?.phrases;
  if (!phrases || phrases.length === 0) return { ...NOT_STALE, refresh };

  // Compare against what the PRODUCER would bake, not raw `translatedValue`: it
  // folds in punctuation that gets no caption row of its own, so every folded
  // row would otherwise read as permanently stale. Same resolver the sync
  // command uses — one implementation, so the two cannot disagree.
  const editor = rootStore.editorStore?.editor;
  // `IVideoEditor` narrows the active transcript to the legacy element alias,
  // but `VideoEditor.getActiveTranscript` just forwards `getActiveTranscriptNew`
  // — same values. Cast at this boundary rather than widen the interface.
  // eslint-disable-next-line no-restricted-syntax
  const tokens = (editor?.getActiveTranscript() ?? []) as TranscriptToken[];
  const captionTextByLeadIndex = tokens.length
    ? resolvePhraseCaptionTexts(
        tokens,
        phrases,
        makeTranslatedValueResolver(phrases),
      )
    : undefined;

  const { isStale, staleRowCount } = detectStaleTranslatedCaptions(
    lines,
    phrases,
    { captionTextByLeadIndex },
  );
  return { isStale, staleRowCount, refresh };
}
